import { useState, useEffect } from 'react';
import { meetingsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Calendar, Plus, Video, Trash2, ExternalLink, CheckCircle, XCircle, Clock } from 'lucide-react';
import { format, isPast, isFuture } from 'date-fns';

function StatusBadge({ status }) {
  const map = {
    confirmed: { label: 'Confirmed', color: 'green' },
    pending:   { label: 'Pending',   color: 'amber' },
    cancelled: { label: 'Cancelled', color: 'gray'  },
    completed: { label: 'Completed', color: 'blue'  },
  };
  const { label, color } = map[status] || map.confirmed;
  return <span className={`badge badge-${color}`}>{label}</span>;
}

function NewMeetingModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    guestName: '', guestEmail: '', guestPhone: '',
    title: '', description: '',
    startTime: '', endTime: '',
    duration: 30, notes: '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.guestName)  return toast.error('Guest name is required');
    if (!form.guestEmail) return toast.error('Guest email is required');
    if (!form.startTime)  return toast.error('Start time is required');

    // Auto-calculate end time if not set
    let endTime = form.endTime;
    if (!endTime && form.startTime) {
      const end = new Date(new Date(form.startTime).getTime() + form.duration * 60000);
      endTime = end.toISOString().slice(0, 16);
    }

    setSaving(true);
    try {
      const { data } = await meetingsAPI.create({ ...form, endTime });
      toast.success('Meeting scheduled!');
      onCreated(data.meeting);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create meeting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20 }}>
      <div className="card" style={{ width:'100%',maxWidth:520,maxHeight:'90vh',overflowY:'auto' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
          <h2 style={{ fontFamily:'var(--font-head)',fontSize:18,fontWeight:700 }}>Schedule Meeting</h2>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>

        <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            <div>
              <label style={{ fontSize:13,color:'var(--text2)',display:'block',marginBottom:5 }}>Guest Name *</label>
              <input className="input" value={form.guestName} onChange={e => set('guestName', e.target.value)} placeholder="John Smith" />
            </div>
            <div>
              <label style={{ fontSize:13,color:'var(--text2)',display:'block',marginBottom:5 }}>Guest Email *</label>
              <input className="input" type="email" value={form.guestEmail} onChange={e => set('guestEmail', e.target.value)} placeholder="john@example.com" />
            </div>
          </div>

          <div>
            <label style={{ fontSize:13,color:'var(--text2)',display:'block',marginBottom:5 }}>Guest Phone</label>
            <input className="input" value={form.guestPhone} onChange={e => set('guestPhone', e.target.value)} placeholder="+1 234 567 8900" />
          </div>

          <div>
            <label style={{ fontSize:13,color:'var(--text2)',display:'block',marginBottom:5 }}>Meeting Title</label>
            <input className="input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Discovery call, Demo, Consultation..." />
          </div>

          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            <div>
              <label style={{ fontSize:13,color:'var(--text2)',display:'block',marginBottom:5 }}>Start Time *</label>
              <input className="input" type="datetime-local" value={form.startTime} onChange={e => set('startTime', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize:13,color:'var(--text2)',display:'block',marginBottom:5 }}>Duration</label>
              <select className="input" value={form.duration} onChange={e => set('duration', Number(e.target.value))}>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize:13,color:'var(--text2)',display:'block',marginBottom:5 }}>Notes</label>
            <textarea className="input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes about this meeting..." style={{ resize:'vertical' }} />
          </div>

          <div style={{ display:'flex',gap:10,justifyContent:'flex-end',marginTop:4 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Scheduling…' : 'Schedule Meeting'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Meetings() {
  const [meetings, setMeetings]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [calConnected, setCalConnected] = useState(false);
  const [filter, setFilter]             = useState('all');

  useEffect(() => {
    fetchMeetings();
    fetchCalendarStatus();
  }, []);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const { data } = await meetingsAPI.list();
      setMeetings(data.meetings || []);
    } catch {
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const { data } = await meetingsAPI.calendarStatus();
      setCalConnected(data.connected);
    } catch {}
  };

  const connectGoogle = async () => {
    try {
      const { data } = await meetingsAPI.googleAuthUrl();
      window.location.href = data.url;
    } catch {
      toast.error('Failed to start Google auth');
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    try {
      await meetingsAPI.googleDisconnect();
      setCalConnected(false);
      toast.success('Google Calendar disconnected');
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      await meetingsAPI.delete(id);
      setMeetings(m => m.filter(x => x._id !== id));
      toast.success('Meeting deleted');
    } catch {
      toast.error('Failed to delete meeting');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await meetingsAPI.update(id, { status });
      setMeetings(m => m.map(x => x._id === id ? data.meeting : x));
    } catch {
      toast.error('Failed to update status');
    }
  };

  const filtered = meetings.filter(m => {
    if (filter === 'upcoming')  return isFuture(new Date(m.startTime)) && m.status !== 'cancelled';
    if (filter === 'past')      return isPast(new Date(m.startTime));
    if (filter === 'cancelled') return m.status === 'cancelled';
    return true;
  });

  const upcoming = meetings.filter(m => isFuture(new Date(m.startTime)) && m.status !== 'cancelled').length;

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12 }}>
        <div>
          <h1>Meetings</h1>
          <p>{upcoming} upcoming meeting{upcoming !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display:'flex',gap:10 }}>
          {calConnected ? (
            <button className="btn btn-secondary btn-sm" onClick={disconnectGoogle} style={{ color:'var(--text3)',fontSize:12 }}>
              ✓ Google Calendar connected
            </button>
          ) : (
            <button className="btn btn-secondary" onClick={connectGoogle}>
              <Calendar size={15}/> Connect Google Calendar
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={16}/> New Meeting
          </button>
        </div>
      </div>

      {/* Google Calendar banner */}
      {!calConnected && (
        <div className="card" style={{ marginBottom:20,background:'rgba(26,158,92,0.06)',border:'1px solid rgba(26,158,92,0.2)',display:'flex',alignItems:'center',gap:14,flexWrap:'wrap' }}>
          <Calendar size={20} style={{ color:'var(--brand)',flexShrink:0 }}/>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600,fontSize:14,marginBottom:2 }}>Connect Google Calendar</div>
            <div style={{ fontSize:13,color:'var(--text2)' }}>Automatically create Google Meet links and send calendar invites to guests.</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={connectGoogle}>Connect now</button>
        </div>
      )}

      {/* Filters */}
      <div className="tabs" style={{ marginBottom:20 }}>
        {['all','upcoming','past','cancelled'].map(f => (
          <button key={f} className={`tab${filter===f?' active':''}`} onClick={() => setFilter(f)} style={{ textTransform:'capitalize' }}>{f}</button>
        ))}
      </div>

      {/* Meetings list */}
      {loading ? (
        <div style={{ textAlign:'center',padding:60,color:'var(--text3)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize:48 }}>📅</div>
            <h3>No meetings yet</h3>
            <p>Schedule your first meeting and it will appear here.</p>
            <button className="btn btn-primary" style={{ marginTop:8 }} onClick={() => setShowModal(true)}>
              <Plus size={16}/> Schedule a meeting
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
          {filtered.map(meeting => {
            const start = new Date(meeting.startTime);
            const past  = isPast(start);

            return (
              <div key={meeting._id} className="card" style={{ display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap' }}>
                {/* Date block */}
                <div style={{ minWidth:56,textAlign:'center',background:'var(--surface2)',borderRadius:'var(--radius)',padding:'8px 4px' }}>
                  <div style={{ fontSize:11,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.06em' }}>{format(start,'MMM')}</div>
                  <div style={{ fontSize:24,fontWeight:700,lineHeight:1 }}>{format(start,'d')}</div>
                </div>

                {/* Details */}
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:4 }}>
                    <span style={{ fontWeight:600,fontSize:15 }}>{meeting.title}</span>
                    <StatusBadge status={past && meeting.status === 'confirmed' ? 'completed' : meeting.status}/>
                  </div>
                  <div style={{ fontSize:13,color:'var(--text2)',marginBottom:6 }}>
                    <Clock size={12} style={{ display:'inline',marginRight:4 }}/>
                    {format(start,'h:mm a')} · {meeting.duration} min · {meeting.guestName} ({meeting.guestEmail})
                  </div>
                  {meeting.googleMeetLink && (
                    <a href={meeting.googleMeetLink} target="_blank" rel="noreferrer"
                      style={{ display:'inline-flex',alignItems:'center',gap:5,fontSize:12,color:'var(--brand)',textDecoration:'none' }}>
                      <Video size={12}/> Join Google Meet
                    </a>
                  )}
                  {meeting.notes && (
                    <div style={{ fontSize:12,color:'var(--text3)',marginTop:4 }}>{meeting.notes}</div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display:'flex',gap:8,flexShrink:0 }}>
                  {meeting.calendarLink && (
                    <a href={meeting.calendarLink} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm btn-icon" title="View in Google Calendar">
                      <ExternalLink size={14}/>
                    </a>
                  )}
                  {meeting.status === 'confirmed' && !past && (
                    <button className="btn btn-ghost btn-sm btn-icon" title="Mark cancelled"
                      onClick={() => updateStatus(meeting._id, 'cancelled')}>
                      <XCircle size={14} style={{ color:'var(--red)' }}/>
                    </button>
                  )}
                  {meeting.status === 'cancelled' && (
                    <button className="btn btn-ghost btn-sm btn-icon" title="Restore"
                      onClick={() => updateStatus(meeting._id, 'confirmed')}>
                      <CheckCircle size={14} style={{ color:'var(--brand)' }}/>
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm btn-icon" title="Delete"
                    onClick={() => deleteMeeting(meeting._id)}>
                    <Trash2 size={14} style={{ color:'var(--red)' }}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <NewMeetingModal
          onClose={() => setShowModal(false)}
          onCreated={m => setMeetings(prev => [m, ...prev])}
        />
      )}
    </div>
  );
}
